import { Box } from '@material-ui/core';
import isEqual from 'lodash.isequal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { UploadImageModal } from '@components';
import { content, page } from '@content';
import {
  ContentCenterType,
  DataVisualizationListItemSchema,
  ModulesSearchParams,
  dataVisualization,
  imageLibrary,
  modules,
  Module,
  Image as ImageModule,
} from '@modules';
import { history, useAppDispatch } from '@store';
import { textTemplate, useHistory, useWebTitle } from '@utils';
import { ContentLibrary, DuplicateModuleModal } from '@views';
import { TopBarContext } from '@layouts/Private';
import { ContentCenterFilters } from './ContentCenterFilters';
import { UploadModuleModal, UploadModuleModalProps } from './UploadModuleModal';

import { ChosenImage, DEFAULT_CHOSEN_DV, DEFAULT_CHOSEN_IMAGE } from '@routes/TemplateView';
import { UploadFormValues } from '@routes/TemplateView/LibrariesPanel/ImageLibraryPanel/ImageLibraryPanel.types';
import { ITEMS_PER_PAGE, SEARCH_QUERY_INIT, SEARCH_DEBOUNCE_DELAY_MS } from './ContentCenter.const';
import { useStyles } from './ContentCenter.styles';
import { UploadVisualizationModal, UploadVisualizationModalProps } from './UploadVisualizationModal';
import { broadcaster, messageReceiver } from '@utils/network/broadcast';

import useDebounceLast from '@utils/hooks/useDebounce';

import { ContentCenterProps } from './ContentCenter.types';
// import { isModule } from './ContentCenter.utils';

/**
 ContentCenter route component.
 @returns {JSX.Element}
 */

export const ContentCenter = ({ libraryType = '' }: ContentCenterProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const { businessUnit: businessUnitId } = useHistory().query;
  // Use libraryType prop for contentCenterType
  const contentCenterType = libraryType as ContentCenterType;

  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [uploadModuleModalOpen, setUploadModuleModalOpen] = useState(false);
  const [uploadImageModalOpen, setUploadImageModalOpen] = useState(false);
  const [isTemplateEditorVisible, setIsTemplateEditorVisible] = useState(false);
  const [isImageEditorVisible, setIsImageEditorVisible] = useState(false);
  const [isDVEditorVisible, setIsDVEditorVisible] = useState(false);
  const [curModule, setCurModule] = useState<Module | DataVisualizationListItemSchema | ImageModule | null>(null);
  // Add detailModule state
  const [detailModule, setDetailModule] = useState<{ id: number } | null>(null);
  // Map LibraryType to ContentLibraryType for ContentLibrary component
  const contentLibraryType = 'fourGrid';
  const { businessUnit } = useHistory().query;
  const [{ searchTerm }, setTopBarCtx] = useContext(TopBarContext);

  const [chosenImage, setChosenImage] = useState<ChosenImage>(DEFAULT_CHOSEN_IMAGE);
  const [chosenDV, setChosenDV] = useState<DataVisualizationListItemSchema>(DEFAULT_CHOSEN_DV);
  const [uploadVisualOpen, setUploadVisualOpen] = useState(false);
  const [showSearchFilterSortUpload, setShowSearchFilterSortUpload] = useState(false);
  const [query, setQuery] = useState<ModulesSearchParams>({
    ...SEARCH_QUERY_INIT,
    keyword: searchTerm,
    ...(businessUnitId ? { businessUnitId } : null),
  });

  const libraryData = modules.useLibraryData();

  const libraryItems = useMemo(() => {
    return libraryData.items.map((item) => {
      const tagParts = item.tag.index.split('-');
      const formattedName =
        tagParts.length === 3
          ? item.templateName
          : `${item.templateName ?? (item as unknown as { moduleTemplateName?: string }).moduleTemplateName} - ${item.name}`;

      return {
        ...item,
        formattedName,
      };
    });
  }, [libraryData.items]);

  const [DVItems, setDVItems] = useState<DataVisualizationListItemSchema[]>([]);
  const dataVisualizations = dataVisualization.useDataVisualizationListData();
  const libraryImages = imageLibrary.useAllData();
  const modulesList =
    contentCenterType === 'imageLibrary'
      ? libraryImages.items
      : contentCenterType === 'dataVisualizationLibrary'
        ? DVItems
        : libraryItems;

  const pageCount = useMemo(() => {
    const totalPageCount =
      contentCenterType === 'imageLibrary'
        ? libraryImages.pageCount
        : contentCenterType === 'dataVisualizationLibrary'
          ? 1
          : libraryData.pageCount;

    return totalPageCount;
  }, [contentCenterType, libraryImages.pageCount, libraryData.pageCount]);

  // reset search term when libraryType changes
  useEffect(() => {
    setTopBarCtx((prev) => ({ ...prev, searchTerm: '' }));
    setQuery((prev) => ({
      ...prev,
      keyword: '',
    }));
  }, [libraryType, setTopBarCtx]);

  // reset the library when the search term is cleared
  useEffect(() => {
    if (searchTerm === '' && businessUnitId) {
      switch (contentCenterType) {
        case 'contentModuleLibrary':
          // Reset modules/content library
          dispatch(modules.actions.resetLibrary());
          dispatch(modules.thunk.search({ ...SEARCH_QUERY_INIT, businessUnitId }));
          break;
        case 'imageLibrary':
          // Reset image library
          dispatch(imageLibrary.actions.resetLibrary());
          dispatch(imageLibrary.thunk.search({ ...SEARCH_QUERY_INIT, businessUnit: businessUnitId, keyword: '' }));

          break;
        case 'dataVisualizationLibrary':
          // Reset data visualizations
          dispatch(dataVisualization.actions.resetDataVisualizationList());
          dispatch(
            dataVisualization.thunk.getListDataVisualization({ ...SEARCH_QUERY_INIT, businessUnit: businessUnitId }),
          );
          break;
        default:
          break;
      }
    }
  }, [searchTerm, dispatch, businessUnitId, contentCenterType]);

  // Determines whether ContentCenterFilters should display the SearchFilterSortUpload component
  useEffect(() => {
    if (isTemplateEditorVisible || isImageEditorVisible || isDVEditorVisible) {
      setShowSearchFilterSortUpload(false);
    } else {
      setShowSearchFilterSortUpload(true);
    }
  }, [isTemplateEditorVisible, isImageEditorVisible, isDVEditorVisible]);

  useEffect(() => {
    const DVs = JSON.parse(JSON.stringify(dataVisualizations.items ?? [])) as DataVisualizationListItemSchema[];

    setDVItems([]);

    if (DVs.length === 0) return;

    let loadedCount = 0;
    const processed: DataVisualizationListItemSchema[] = [];

    DVs.forEach((dataVisual, idx) => {
      if (dataVisual?.thumbnailPath) {
        const image = new Image();

        image.onload = function () {
          dataVisual.width = image.width;
          dataVisual.height = image.height;
          dataVisual.thumbnailPath = `${dataVisual.thumbnailPath}?${new Date().getTime()}`;
          processed[idx] = dataVisual;
          loadedCount++;
          if (loadedCount === DVs.length) {
            // All images processed, sort and set
            setDVItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
          }
        };
        image.onerror = () => {
          processed[idx] = dataVisual;
          loadedCount++;
          if (loadedCount === DVs.length) {
            setDVItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
          }
        };

        image.src = dataVisual.thumbnailPath as string;
      } else {
        processed[idx] = dataVisual;
        loadedCount++;
        if (loadedCount === DVs.length) {
          setDVItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      }
    });
  }, [dataVisualizations.items, setDVItems]);

  useWebTitle(textTemplate(content.entityManager, { entity: content.content }));

  useEffect(() => {
    if (!businessUnitId) {
      history.push(page.dashboard);
      return undefined;
    }

    return () => {
      dispatch(modules.actions.resetClone());
    };
  }, [businessUnitId, dispatch]);

  useEffect(() => {
    dispatch(modules.actions.resetLibrary());
  }, [dispatch]);

  const getLibraryItems = useCallback(
    async (selectedContentCenterType: ContentCenterType | '', searchQuery: ModulesSearchParams) => {
      if (selectedContentCenterType) {
        if (searchQuery.page > pageCount) {
          setQuery((prev) => ({
            ...prev,
            page: pageCount,
          }));

          return;
        }

        if (selectedContentCenterType === 'imageLibrary') {
          // if we are in the image editor, do not fetch images again
          if (isImageEditorVisible) {
            return;
          }

          setQuery((prev) => ({ ...prev, sortBy: 'name' }));

          const searchKeyword = searchQuery.keyword ?? '';
          await dispatch(
            imageLibrary.thunk.search({
              ...searchQuery,
              businessUnit: searchQuery.businessUnitId,
              keyword: searchKeyword, // query.keyword!,
              // sortBy: 'name',
            }),
          );

          setDVItems([]);
        } else if (selectedContentCenterType === 'dataVisualizationLibrary') {
          // if the DV editor is visible, do not fetch data visualizations again
          if (isDVEditorVisible) {
            return;
          }
          dispatch(dataVisualization.actions.resetDataVisualizationList());
          await dispatch(
            dataVisualization.thunk.getListDataVisualization({
              ...searchQuery,
              businessUnit: searchQuery.businessUnitId,
            }),
          );
        } else {
          // otherwise "contentModuleLibrary" | "templates"
          // show the content modules
          await dispatch(modules.thunk.search(searchQuery));
          setDVItems([]);
        }
      }
    },
    [dispatch, isDVEditorVisible, isImageEditorVisible, pageCount],
  );

  // run the search on initial load for content modules
  useEffect(() => {
    getLibraryItems(contentCenterType, query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if (contentCenterType === 'contentModuleLibrary') {
    messageReceiver.onmessage = () => {
      dispatch(modules.actions.resetLibrary());
      setQuery((prev) => ({
        ...prev,
        page: 1,
      }));
      dispatch(modules.thunk.search({ ...query, page: 1 }));
    };
    // } else {
    //   messageReceiver.onmessage = () => {};
    // }
    // Only run when dispatch, query, setQuery, or contentCenterType changes
  }, [dispatch, query, setQuery, contentCenterType]);

  useEffect(() => {
    return () => {
      // messageReceiver.close();
      dispatch(modules.actions.resetLibrary());
      dispatch(imageLibrary.actions.resetLibrary());
      dispatch(dataVisualization.actions.resetDataVisualizationList());
    };
  }, [dispatch]);

  const filterSearch = useCallback(
    (values: Record<string, unknown>) => {
      setChosenDV(DEFAULT_CHOSEN_DV);
      dispatch(modules.actions.resetLibrary());
      dispatch(imageLibrary.actions.resetLibrary());
      dispatch(dataVisualization.actions.resetDataVisualizationList());

      // Removed legacy setters for contentCenterType, libraryType, and detailModule
      setQuery((prev) => ({
        ...prev,
        ...values,
        businessUnitId:
          typeof businessUnitId === 'number' && !isNaN(businessUnitId) ? businessUnitId : prev.businessUnitId,
        page: 1,
      }));
      const searchQuery = {
        ...query,
        ...values,
        businessUnitId:
          typeof businessUnitId === 'number' && !isNaN(businessUnitId) ? businessUnitId : query.businessUnitId,
        page: 1,
      };
      getLibraryItems(contentCenterType, searchQuery as ModulesSearchParams);
    },
    [businessUnitId, dispatch, getLibraryItems, query, contentCenterType],
  );

  // const [debouncedSearch] = useDebounceLast(async (values: Partial<ModulesSearchParams>) => {
  //   filterSearch(values);
  // }, SEARCH_DEBOUNCE_DELAY_MS);

  const debouncedSearchCallback = useCallback(
    async (values: Partial<ModulesSearchParams>) => {
      filterSearch(values);
    },
    [filterSearch],
  );

  const [debouncedSearch] = useDebounceLast(debouncedSearchCallback, SEARCH_DEBOUNCE_DELAY_MS);

  const handleFilterSearch = useCallback(
    (values: Record<string, unknown>) => {
      debouncedSearch(values).catch((err) => {
        // console.error(err);
      });
    },
    [debouncedSearch],
  );

  const handlePageChange = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));

    const searchQuery = { ...query, page: query.page + 1 };
    getLibraryItems(contentCenterType, searchQuery);
  }, [contentCenterType, getLibraryItems, query]);

  const openModal = (module: { id: number }, moduleType: string) => {
    // Only update detailModule local state
    if (moduleType === 'instance' || moduleType === 'image') {
      return;
    }
    setDetailModule(module as { id: number });
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
  };

  const handleUploadModuleModalOpen = useCallback(() => {
    switch (contentCenterType) {
      case 'contentModuleLibrary':
        setUploadModuleModalOpen(true);
        break;
      case 'imageLibrary':
        setUploadImageModalOpen(true);
        break;
      case 'dataVisualizationLibrary':
        setUploadVisualOpen(true);
        break;
      default:
        break;
    }
  }, [setUploadModuleModalOpen, contentCenterType]);

  const handleUploadModuleModalClose = useCallback(() => {
    if (contentCenterType !== 'imageLibrary') {
      setUploadModuleModalOpen(false);

      return;
    }

    setUploadImageModalOpen(false);
  }, [setUploadModuleModalOpen, contentCenterType]);

  const handleUploadModule: UploadModuleModalProps['onSubmit'] = useCallback(
    async (values) => {
      if (businessUnitId) {
        if (!isEqual(query, SEARCH_QUERY_INIT)) {
          await dispatch(modules.thunk.search({ ...SEARCH_QUERY_INIT, businessUnitId }));
        }

        const result = await dispatch(modules.thunk.create({ ...values, businessUnitId }));

        if (modules.thunk.create.fulfilled.match(result)) {
          handleUploadModuleModalClose();

          setTimeout(() => {
            broadcaster.postMessage('refresh');
          }, 500);
        }
      }
    },
    [businessUnitId, dispatch, handleUploadModuleModalClose, query],
  );

  const handleCreateImage = useCallback(
    async (file: File | null, businessUnitItem: number) => {
      const body = new FormData();

      body.append('file', file as Blob);

      const result = await dispatch(
        imageLibrary.thunk.create({ params: { businessUnit: businessUnitItem }, body, itemsPerPage: ITEMS_PER_PAGE }),
      );

      if (result.payload && imageLibrary.thunk.create.fulfilled.match(result)) {
        handleUploadModuleModalClose();
      }
    },
    [dispatch, handleUploadModuleModalClose],
  );

  const handleUploadImageSubmit = useCallback(
    async (values: UploadFormValues) => {
      if (businessUnit && Array.isArray(values.files)) {
        for (const file of values.files) {
          await handleCreateImage(file, businessUnit);
        }
      } else if (businessUnit && !Array.isArray(values.files)) {
        await handleCreateImage(values.files, businessUnit);
      }
    },
    [handleCreateImage, businessUnit],
  );

  const handleTemplateView = (module: any, moduleType: string) => {
    if (moduleType === 'image') {
      setIsImageEditorVisible(true);
      const img = module as {
        id: number;
        width: number;
        height: number;
        name: string;
        path: string;
      };
      setChosenImage({
        id: img.id,
        width: img.width,
        height: img.height,
        name: img.name,
        imageSrc: img.path,
        chosenModuleElemId: null,
        link: '',
        title: '',
        altText: '',
      });
    }

    if (moduleType === 'instance') {
      setIsTemplateEditorVisible(true);
      setCurModule(module);
    }

    if (moduleType === 'template') {
      setIsTemplateEditorVisible(true);
      setCurModule(module);
    }

    if (moduleType === 'dataVisual') {
      setIsDVEditorVisible(true);
      setCurModule(module);
      const dv = module as import('@modules').DataVisualizationListItemSchema;
      setChosenDV(dv);
    }

    setDetailModule(module as { id: number });
  };

  // Removed legacy setDetailModule calls for chosenDV and chosenImage

  const handleUploadVisualModalClose = useCallback(() => {
    setUploadVisualOpen(false);
  }, [setUploadVisualOpen]);

  const handleFormVisualSubmit: UploadVisualizationModalProps['onSubmit'] = useCallback(
    async (values) => {
      const valuesWithClient = { ...values, 'businessUnit[id]': businessUnitId };
      valuesWithClient.isMaxScaleEnabled = !!valuesWithClient.isMaxScaleEnabled;

      const formData = new FormData();

      Object.keys(valuesWithClient).forEach((key) => {
        formData.append(key, valuesWithClient[key as keyof typeof valuesWithClient] as string | Blob);
      });

      const result = await dispatch(dataVisualization.thunk.create(formData));

      if (dataVisualization.thunk.create.fulfilled.match(result)) {
        setTimeout(() => {
          dispatch(
            dataVisualization.thunk.getListDataVisualization({
              ...query,
              businessUnit: query.businessUnitId,
            }),
          );
        }, 2000);
        handleUploadVisualModalClose();
      }
    },
    [businessUnitId, query, dispatch, handleUploadVisualModalClose],
  );

  const updateBreadcrumbs = useCallback(
    (resetBreadcrumbs = false) => {
      const breadcrumbs: string[] = [];
      breadcrumbs.push(textTemplate(content.entityManager, { entity: content.content }));

      switch (libraryType) {
        case 'contentModuleLibrary':
          breadcrumbs.push(content.contentModules);
          break;
        case 'imageLibrary':
          breadcrumbs.push(content.images);
          break;
        case 'dataVisualizationLibrary':
          breadcrumbs.push(content.dataVisualizations);
          break;
        default:
          break;
      }

      // if (!resetBreadcrumbs) {
      //   switch (libraryType) {
      //     case 'contentModuleLibrary':
      //       if (curModule && isModule(curModule)) {
      //         if (curModule.templateName) {
      //           breadcrumbs.push(curModule.templateName);
      //         }
      //       }
      //       break;
      //     case 'imageLibrary':
      //       if (chosenImage && chosenImage.name) {
      //         breadcrumbs.push(chosenImage.name);
      //       }
      //       break;
      //     case 'dataVisualizationLibrary':
      //       if (chosenDV && chosenDV.name) {
      //         breadcrumbs.push(chosenDV.name);
      //       }
      //       break;
      //     default:
      //       break;
      //   }
      // }

      setTopBarCtx((prev) => ({
        ...prev,
        breadcrumbs,
      }));
    },
    [libraryType, setTopBarCtx],
  );

  useEffect(() => {
    if (!isTemplateEditorVisible && !isImageEditorVisible && !isDVEditorVisible) {
      updateBreadcrumbs(true);
    } else {
      updateBreadcrumbs(false);
    }
    // Only run when editor visibility changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTemplateEditorVisible, isImageEditorVisible, isDVEditorVisible, updateBreadcrumbs]);

  // Context values for SearchFilterSortUpload component
  useEffect(() => {
    setTopBarCtx((prev) => ({
      ...prev,
      contentCenterType: libraryType,
      setIsDVEditorVisible,
      setIsImageEditorVisible,
      setIsTemplateEditorVisible,
      showSearchFilterSortUpload,
      onSearch: handleFilterSearch,
      onUploadModule: handleUploadModuleModalOpen,
    }));

    return () => {
      // reset SearchFilterSortUpload related context values on unmount
      setTopBarCtx((prev) => ({
        ...prev,
        // contentCenterType: '',
        // searchTerm: '',
        showSearchFilterSortUpload: false,
      }));
    };
  }, [
    handleFilterSearch,
    handleUploadModuleModalOpen,
    setIsDVEditorVisible,
    setIsImageEditorVisible,
    setIsTemplateEditorVisible,
    libraryType,
    setTopBarCtx,
    showSearchFilterSortUpload,
  ]);

  return (
    <Box className={styles.contentCenter}>
      <Box className={styles.contentCenterLeft}>
        {/* <ContentCenterFilters
          showSearchFilterSortUpload={showSearchFilterSortUpload}
          type={contentCenterType}
          setIsDVEditorVisible={setIsDVEditorVisible}
          setIsImageEditorVisible={setIsImageEditorVisible}
          setIsTemplateEditorVisible={setIsTemplateEditorVisible}
          onSearch={handleFilterSearch}
          onUploadModule={handleUploadModuleModalOpen}
        /> */}
        {uploadModuleModalOpen && (
          <UploadModuleModal onSubmit={handleUploadModule} onClose={handleUploadModuleModalClose} />
        )}
        {uploadImageModalOpen && (
          <UploadImageModal onSubmit={handleUploadImageSubmit} onClose={handleUploadModuleModalClose} />
        )}
        {uploadVisualOpen && (
          <UploadVisualizationModal onSubmit={handleFormVisualSubmit} onCancel={handleUploadVisualModalClose} />
        )}
        <ContentLibrary
          type={contentLibraryType}
          activeModuleId={detailModule ? detailModule.id : undefined}
          modulesList={modulesList}
          currentPage={query.page}
          pageCount={pageCount}
          chosenImage={chosenImage}
          chosenDV={chosenDV}
          query={query}
          isImageEditorVisible={isImageEditorVisible}
          curModule={curModule}
          isDVEditorVisible={isDVEditorVisible}
          isTemplateEditorVisible={isTemplateEditorVisible}
          setIsTemplateEditorVisible={setIsTemplateEditorVisible}
          setChosenImage={setChosenImage}
          setChosenDV={setChosenDV}
          setIsImageEditorVisible={setIsImageEditorVisible}
          setIsDVEditorVisible={setIsDVEditorVisible}
          onViewDetail={openModal}
          onTemplateView={handleTemplateView}
          onPageChange={handlePageChange}
        />
      </Box>
      {showDuplicateModal && detailModule && (
        <DuplicateModuleModal detailModuleId={detailModule.id} onClose={handleCloseDuplicateModal} />
      )}
    </Box>
  );
};
